import { Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminOrders from "./pages/admin/Orders";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminUsers from "./pages/admin/Users";
import AdminCouriers from "./pages/admin/Couriers";
import AdminSettings from "./pages/admin/Settings";
import CourierDashboard from "./pages/courier/Dashboard";
import CourierDeliveries from "./pages/courier/Deliveries";
import CourierEarnings from "./pages/courier/Earnings";
import CustomerDashboard from "./pages/customer/Dashboard";
import CustomerOrders from "./pages/customer/Orders";
import CustomerNewOrder from "./pages/customer/NewOrder";
import CustomerTracking from "./pages/customer/Tracking";
import CustomerInvoices from "./pages/customer/CustomerInvoices";
import CustomerLiveTrack from "./pages/customer/CustomerLiveTrack";
import Profile from "./pages/Profile";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function useAuth() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setIsAuthenticated(true);
      } catch (e) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  if (loading) {
    return { user: null, isAuthenticated: false, loading: true };
  }

  return { user, isAuthenticated, loading };
}

function App() {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  const getDashboardRoute = () => {
    switch (user?.role) {
      case "admin":
        return "/admin/dashboard";
      case "courier":
        return "/courier/dashboard";
      case "customer":
        return "/customer/dashboard";
      default:
        return "/login";
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={getDashboardRoute()} replace />}
        />

        {/* Admin Routes */}
        <Route element={<Layout allowedRole="admin" />}>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/invoices" element={<AdminInvoices />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/couriers" element={<AdminCouriers />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
        </Route>

        {/* Courier Routes */}
        <Route element={<Layout allowedRole="courier" />}>
          <Route path="/courier/dashboard" element={<CourierDashboard />} />
          <Route path="/courier/deliveries" element={<CourierDeliveries />} />
          <Route path="/courier/earnings" element={<CourierEarnings />} />
        </Route>

        {/* Customer Routes */}
        <Route element={<Layout allowedRole="customer" />}>
          <Route path="/customer/dashboard" element={<CustomerDashboard />} />
          <Route path="/customer/orders" element={<CustomerOrders />} />
          <Route path="/customer/new-order" element={<CustomerNewOrder />} />
          <Route path="/customer/tracking/:id" element={<CustomerTracking />} />
          <Route path="/customer/invoices" element={<CustomerInvoices />} />
          <Route
            path="/customer/live-track/:id"
            element={<CustomerLiveTrack />}
          />
        </Route>

        {/* Shared Routes */}
        <Route element={<Layout />}>
          <Route path="/profile" element={<Profile />} />
        </Route>

        <Route
          path="*"
          element={<Navigate to={getDashboardRoute()} replace />}
        />
      </Routes>
    </QueryClientProvider>
  );
}

export default App;
